import { Injectable } from '@angular/core';
import { ExposureControlType, GuardFeatures, IFeatureValue } from './ec.typings';
import { BehaviorSubject } from 'rxjs';
import { GlobalVariables } from '../globals';

@Injectable()
export abstract class ExposureControlService {
    public featuresList: IFeatureValue[] = [];
    protected _ecCache = new Map<string, string | boolean>();
    public abstract getFeatureValue(feature: IFeatureValue, baseUrl?: string): Promise<string | boolean>;
    public abstract checkFeatures(url?: string): Promise<void>;

    // #region shared feature value getter
    /**
     * Indicates whether factory or workspace has at least one Manage Virtual Network resource.
     */
    public isVNetPresent$ = new BehaviorSubject<boolean>(undefined);

    public get enableADFSparkGateway() {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFUseSparkGatewayInAdf,
            GlobalVariables.enableADFSparkGatewayWithFF
        );
    }

    public get enableADFSparkGatewayManagedVnet(): boolean {
        return (
            this.enableADFSparkGateway &&
            this.getCombinedFeatureValue<boolean>(
                GuardFeatures.ADFSparkGatewayManagedVnet,
                GlobalVariables.enableADFSparkGatewayManagedVnetWithFF
            )
        );
    }

    public get enableADFSparkGatewayMultiRegion(): boolean {
        return (
            this.enableADFSparkGateway &&
            this.getCombinedFeatureValue<boolean>(
                GuardFeatures.ADFSparkGatewayMultiRegion,
                GlobalVariables.enableADFSparkGatewayMultiRegionWithFF
            )
        );
    }

    public get enableADFSparkGatewayQuickReuse(): boolean {
        return (
            this.enableADFSparkGateway &&
            this.getCombinedFeatureValue<boolean>(
                GuardFeatures.ADFSparkGatewayQuickReuse,
                GlobalVariables.enableADFSparkGatewayQuickReuseWithFF
            )
        );
    }

    public get enableSsisIrSharing(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.UX_EnableSsisIRSharing,
            GlobalVariables.enableSsisIrSharingWithFF
        );
    }

    public get airflow(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFIntegrationRuntimeAirflow,
            GlobalVariables.airflowWithFF
        );
    }

    public get enableGoogleCloudStorageSourceDataFlow(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFDataflowGoogleCloudStorageSource,
            GlobalVariables.enableGoogleCloudStorageSourceDataFlowWithFF
        );
    }

    public get enableScopeActivity(): boolean {
        return this.getCachedFeatureValue<boolean>(GuardFeatures.ADFEnableScopeActivity);
    }

    public get enableADLSCosmosDataset(): boolean {
        return this.getCachedFeatureValue<boolean>(GuardFeatures.ADFEnableADLSCosmosDataset);
    }

    public get enableDatabricksActivity(): boolean {
        return __IS_FABRIC_DPE__
            ? this.getCombinedFeatureValue<boolean>(
                GuardFeatures.ADFEnableDatabricksActivityInTrident,
                GlobalVariables.enableDatabricksActivity
            )
            : false;
    }

    public get enableAzureDatabricksJobActivity(): boolean {
        return __IS_FABRIC_DPE__
            ? this.getCombinedFeatureValue<boolean>(
                GuardFeatures.ADFEnableAzureDatabricksJobActivityInTrident,
                GlobalVariables.enableAzureDatabricksJobActivity
            )
            : false;
    }

    public get enableAzureDatabricksADFJobActivity(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFAzureDatabricksJobActivity,
            GlobalVariables.enableAzureDatabricksADFJobActivity
        )
    }

    public get enableHDInsightActivity(): boolean {
        return __IS_FABRIC_DPE__
            ? this.getCombinedFeatureValue<boolean>(
                GuardFeatures.ADFEnableHDInsightActivityInTrident,
                GlobalVariables.enableHDInsightActivity
            )
            : false;
    }

    public get enableMicrosoft365DataFlow(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFDataflowM365,
            GlobalVariables.enableMicrosoft365DataFlowWithFF
        );
    }

    public get showSapTableRowSkip(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.UX_SupportSapTableRowSkips,
            GlobalVariables.showSapTableRowSkipWithFF
        );
    }

    public get enableCopySapOdp(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.UX_SupportSapCdcConnector,
            GlobalVariables.enableCopySapOdpWithFF
        );
    }

    /**
     * @deprecated: Not in our plan now.
     */
    public get enableOracleDataFlow(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFDataflowOnPremOracle,
            GlobalVariables.enableOracleDataFlowWithFF
        );
    }

    public get enableSAPOData(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFDataflowSapOData,
            GlobalVariables.enableSAPODataWithFF
        );
    }

    public get enableEvergreenSsis(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.UX_SsisIRSupportEvergreen,
            GlobalVariables.enableEvergreenSsis
        );
    }

    public get enableTrino(): boolean {
        return this.getCombinedFeatureValue<boolean>(GuardFeatures.ADMSTrino, GlobalVariables.enableTrinoWithFF);
    }

    public get enableFabricTrialCapacityCreation(): boolean {
        return GlobalVariables.enableFabricCapacitySelectionProvisioning
            && this.getCombinedFeatureValue<boolean>(
                GuardFeatures.UX_EnableFabricCapacityCreation,
                GlobalVariables.enableFabricTrialCapacityCreation
            );
    }

    public get enableSsisIrVNetV2Feature(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.UX_SsisIREnableVNetV2Feature,
            GlobalVariables.enableSsisIrVNetV2FeatureWithFF
        );
    }

    public get enableSharePointOnlineFile(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.UX_SupportSharePointOnlineFile,
            GlobalVariables.enableSharePointOnlineFileWithFF
        );
    }

    public get enableScopeActivityRestriction(): boolean {
        return this.getCachedFeatureValue<boolean>(GuardFeatures.ADFScopeActivtyRestriction);
    }

    public get UseTokenServiceInSJDActivity(): boolean {
        return true;
    }

    public get UseTokenServiceInNotebookActivity(): boolean {
        return true;
    }

    public get UseTokenServiceInDFActivity(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.UseTokenServiceInDFActivity,
            GlobalVariables.enableDFConnection
        );
    }

    public get UseTokenServiceInDataFlowActivity(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.UseTokenServiceInDataflowActivity,
            GlobalVariables.enableDataflowConnection
        );
    }

    public get enableSJDEnvCrossWorkspace(): boolean {
        return true;
    }

    public get enableWIForNBSJDActivities(): boolean {
        return true;
    }

    public get enableWIForCustomMLActivities(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFEnableWIForCustomMLActivities,
            GlobalVariables.enableWIForCustomML
        );
    }

    public get enableWIForDFActivity(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFEnableWIForDFActivity,
            GlobalVariables.enableWIForDF
        );
    }

    public get enableLargeActivityLimit(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.ADFSupportLargeNumberOfActivities,
            GlobalVariables.enableLargeActivityLimit
        );
    }

    public get enablePowerQueryActivity(): boolean {
        return this.getCachedFeatureValue<boolean>(GuardFeatures.ADFEnablePowerQueryActivityInADF);
    }

    public get enableFabricStorageTrigger(): boolean {
        return __IS_FABRIC_DPE__;
    }

    public get enableDbtJobLakehouseAdapter(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.DbtJobLakehouseAdapter,
            GlobalVariables.enableDbtLakehouse
        );
    }

    public get enableDbtJobFabricSqlAdapter(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.DbtJobFabricSqlAdapter,
            GlobalVariables.enableDbtFabricSql
        );
    }

    public get enableApiIntegrationActivity(): boolean {
        return this.getCombinedFeatureValue<boolean>(
            GuardFeatures.EnableApiIntegrationActivity,
            GlobalVariables.enableApiIntegrationActivity
        );
    }

    public get enableFabricActionPicker(): boolean {
        return __IS_FABRIC_DPE__ && (GlobalVariables.enableFabricActionPicker ??
            this.getCachedFeatureValue<boolean>(GuardFeatures.UX_EnableFabricActionPicker));
    }

    public get enableIRInWebhookActivity(): boolean {
        return !__IS_FABRIC_DPE__
            ? this.getCombinedFeatureValue<boolean>(
                GuardFeatures.UX_EnableWebhookOnIR,
                GlobalVariables.enableWebHookOnIR
            )
            : false;
    }

    public get enableTeradataSinkInADF(): boolean {
        return !__IS_FABRIC_DPE__
            ? this.getCombinedFeatureValue<boolean>(
                GuardFeatures.SupportTeradataSinkForNonFabricEnv,
                GlobalVariables.teradataSinkInADF
            ) || this.getCombinedFeatureValue<boolean>(
                GuardFeatures.ADFSupportTeradataSinkForNonFabricEnvUX,
                GlobalVariables.teradataSinkInADF
            ) 
            : false;
    }

    public get enableWorkspaceObjectIdFix(): boolean {
        return __IS_FABRIC_DPE__
            ? this.getCombinedFeatureValue<boolean>(
                GuardFeatures.SupportExploratoryAPIForOAP,
                GlobalVariables.workspaceObjectIdFix
            )
            : false;
    }

    public get showSelectInServiceNowV2(): boolean {
        return this.getCachedFeatureValue<boolean>(GuardFeatures.UX_EnableSelectInServiceNowV2);
    }
    // #endregion

    public getCombinedFeatureValue<T = number | string | boolean>(
        exposureControlName: string,
        featureFlagValue: T,
        ecValueProcessor?: (val: string | boolean) => T
    ): T {
        if (!ecValueProcessor) {
            ecValueProcessor = (val: string | boolean) => (val === true) as T;
        }
        return (this.getCachedFeatureValue(exposureControlName, ecValueProcessor) || featureFlagValue) as T;
    }

    public getCachedFeatureValue<T>(exposureControlName: string, ecValueProcessor?: (val: string | boolean) => T): T {
        if (!ecValueProcessor) {
            ecValueProcessor = (val: string | boolean) => (val === true) as T;
        }
        return ecValueProcessor(this._ecCache.get(exposureControlName));
    }

    protected _parseFeatureValue(featureType: string, featureValue: string) {
        return featureType === ExposureControlType.AllowList || featureType === ExposureControlType.Dictionary
            ? featureValue
            : featureType === ExposureControlType.Feature
                ? featureValue.toLowerCase() === 'true'
                : undefined;
    }
}
